import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  Grid,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import ClearIcon from "@mui/icons-material/Clear";
import { useTheme } from "@mui/material/styles";
import { useUser } from "../../functions/userContext";
import { callAPI } from "../../functions/api";

// Helper functions to convert backend enums to frontend strings
const getPriorityFromEnum = (priorityEnum) => {
  const priorities = ["Info", "Low", "Medium", "High", "Critical"];
  return priorities[priorityEnum] || "Medium";
};

const getRoleFromEnum = (roleEnum) => {
  const roles = ["Responsible", "Accountable", "Consulted", "Informed"];
  return roles[roleEnum] || "Informed";
};

// Mock data - replace with API call later
const mockNotices = [
  {
    id: 1,
    title: "Windows Server 2012 End of Life",
    shortDescription:
      "Microsoft will discontinue support for Windows Server 2012 on October 10, 2023",
    details:
      "All Windows Server 2012 instances must be upgraded or migrated before the deadline. This affects 45 servers across production environments. Migration plans should include testing in staging environments and coordination with application teams.",
    priority: "High",
    deadline: new Date("2023-10-10"), // Overdue
    createdAt: new Date("2024-01-15"),
    isActive: true,
    roleAssignments: [
      {
        role: "Responsible",
        personName: "John Smith",
        department: "Infrastructure",
        email: "john.smith@company.com",
      },
      {
        role: "Accountable",
        personName: "Sarah Johnson",
        department: "IT Operations",
        email: "sarah.johnson@company.com",
      },
      {
        role: "Informed",
        personName: "Mike Davis",
        department: "Security",
        email: "mike.davis@company.com",
      },
    ],
  },
  {
    id: 2,
    title: "SSL Certificate Renewal - *.company.com",
    shortDescription: "Wildcard SSL certificate expires in 3 days",
    details:
      "The wildcard SSL certificate for *.company.com expires soon. This certificate is used across 25+ applications. Renewal process takes 5-7 business days. Coordinate with web team for deployment.",
    priority: "High",
    deadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days from now
    createdAt: new Date("2024-01-10"),
    isActive: true,
    roleAssignments: [
      {
        role: "Responsible",
        personName: "Alex Turner",
        department: "Security",
        email: "alex.turner@company.com",
      },
      {
        role: "Consulted",
        personName: "Web Team",
        department: "Development",
        email: "webteam@company.com",
      },
    ],
  },
  {
    id: 3,
    title: "Oracle Database License Renewal",
    shortDescription: "Oracle database licenses expire in 3 weeks",
    details:
      "Annual Oracle database license renewal required for production and staging environments. Budget approval needed by February 1st. Includes 12 production databases and 8 staging instances.",
    priority: "Medium",
    deadline: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000), // 3 weeks from now
    createdAt: new Date("2024-01-20"),
    isActive: true,
    roleAssignments: [
      {
        role: "Responsible",
        personName: "Lisa Chen",
        department: "Database Team",
        email: "lisa.chen@company.com",
      },
      {
        role: "Accountable",
        personName: "Robert Wilson",
        department: "Finance",
        email: "robert.wilson@company.com",
      },
    ],
  },
  {
    id: 4,
    title: "Data Center Maintenance Window",
    shortDescription: "Scheduled maintenance at primary data center in 2 months",
    details:
      "Planned maintenance window for HVAC system upgrades and power distribution improvements. Expected downtime: 4-6 hours. All critical systems have been migrated to secondary data center.",
    priority: "Low",
    deadline: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000), // 2 months from now
    createdAt: new Date("2024-01-25"),
    isActive: true,
    roleAssignments: [
      {
        role: "Responsible",
        personName: "Data Center Team",
        department: "Facilities",
        email: "datacenter@company.com",
      },
      { role: "Informed", personName: "All Teams", department: "IT", email: "it-all@company.com" },
    ],
  },
  {
    id: 5,
    title: "AWS Cost Optimization Review",
    shortDescription: "Quarterly review of AWS spending and resource optimization",
    details:
      "Conduct comprehensive review of AWS resources, identify unused instances, optimize storage tiers, and review Reserved Instance purchases. Target 15% cost reduction.",
    priority: "Medium",
    deadline: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000), // 45 days from now
    createdAt: new Date("2024-06-01"),
    isActive: true,
    roleAssignments: [
      {
        role: "Responsible",
        personName: "Cloud Team",
        department: "Infrastructure",
        email: "cloud@company.com",
      },
      {
        role: "Accountable",
        personName: "IT Director",
        department: "IT Operations",
        email: "itdirector@company.com",
      },
      {
        role: "Consulted",
        personName: "Finance",
        department: "Finance",
        email: "finance@company.com",
      },
    ],
  },
  {
    id: 6,
    title: "Security Audit Preparation",
    shortDescription: "Annual security audit scheduled in 6 weeks",
    details:
      "Prepare for annual SOC 2 Type II audit. Review access controls, update documentation, conduct vulnerability assessments, and ensure compliance with security policies.",
    priority: "High",
    deadline: new Date(Date.now() + 42 * 24 * 60 * 60 * 1000), // 6 weeks from now
    createdAt: new Date("2024-06-15"),
    isActive: true,
    roleAssignments: [
      {
        role: "Responsible",
        personName: "Security Team",
        department: "Security",
        email: "security@company.com",
      },
      {
        role: "Accountable",
        personName: "CISO",
        department: "Security",
        email: "ciso@company.com",
      },
      {
        role: "Informed",
        personName: "All Department Heads",
        department: "Management",
        email: "deptHeads@company.com",
      },
    ],
  },
  {
    id: 7,
    title: "Network Equipment Firmware Updates",
    shortDescription: "Critical firmware updates for core network switches",
    details:
      "Apply security patches and firmware updates to core network infrastructure. Updates address several CVEs. Maintenance window required during off-peak hours.",
    priority: "Medium",
    deadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 2 weeks from now
    createdAt: new Date("2024-07-01"),
    isActive: true,
    roleAssignments: [
      {
        role: "Responsible",
        personName: "Network Team",
        department: "Infrastructure",
        email: "network@company.com",
      },
      {
        role: "Consulted",
        personName: "Operations",
        department: "IT Operations",
        email: "ops@company.com",
      },
    ],
  },
];

const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "#FF6C58"; // Keep accent for high priority only
    case "medium":
      return "#C3C9D5"; // Subtle gray
    case "low":
      return "#C3C9D5"; // Subtle gray
    default:
      return "#C3C9D5";
  }
};

const getPriorityIcon = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return <PriorityHighIcon />;
    case "medium":
      return <WarningIcon />;
    case "low":
      return <InfoIcon />;
    default:
      return <InfoIcon />;
  }
};

const getRoleColor = (role) => {
  switch (role?.toLowerCase()) {
    case "responsible":
      return "#414243"; // Subtle dark gray
    case "accountable":
      return "#FF6C58"; // Only accountable gets accent color
    case "consulted":
      return "#414243"; // Subtle dark gray
    case "informed":
      return "#414243"; // Subtle dark gray
    default:
      return "#414243";
  }
};

const CountdownTimer = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState({});
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const deadlineTime = new Date(deadline).getTime();
      const difference = deadlineTime - now;

      if (difference > 0) {
        setIsOverdue(false);
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        setIsOverdue(true);
        setTimeLeft({
          days: Math.abs(Math.floor(difference / (1000 * 60 * 60 * 24))),
          hours: Math.abs(Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))),
          minutes: Math.abs(Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))),
          seconds: Math.abs(Math.floor((difference % (1000 * 60)) / 1000)),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [deadline]);

  const formatDeadline = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Determine urgency level for styling
  const now = new Date();
  const daysLeft = Math.floor((new Date(deadline) - now) / (1000 * 60 * 60 * 24));

  const chipStyle = {
    backgroundColor: "#333333",
    color: isOverdue ? "#FF6C58" : daysLeft <= 30 ? "#FFB74D" : "#CCC",
    fontSize: "0.75rem",
    border: "none",
    minWidth: "32px",
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        width: "100%",
      }}
    >
      <AccessTimeIcon sx={{ fontSize: 16, color: "#aaa", mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "#aaa", display: "block", lineHeight: 1.2 }}>
          {isOverdue ? "Overdue by:" : "Time remaining:"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
          {timeLeft.days > 0 && <Chip label={`${timeLeft.days}d`} size="small" sx={chipStyle} />}
          {(timeLeft.hours > 0 || timeLeft.days > 0) && (
            <Chip label={`${timeLeft.hours}h`} size="small" sx={chipStyle} />
          )}
          <Chip label={`${timeLeft.minutes}m`} size="small" sx={chipStyle} />
          <Chip label={`${timeLeft.seconds}s`} size="small" sx={chipStyle} />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "#aaa",
            display: "block",
            mt: 0.5,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Due: {formatDeadline(deadline)}
        </Typography>
      </Box>
    </Box>
  );
};

const NoticesList = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const { ROLE_CONFIG, checkAccess } = useUser();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    shortDescription: "",
    details: "",
    priority: "Medium",
    deadline: "",
    roleAssignments: [],
  });

  // Filter and sort states
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [urgencyFilter, setUrgencyFilter] = useState("All");
  const [sortBy, setSortBy] = useState("deadline");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    // Fetch notices from API
    const fetchNotices = async () => {
      try {
        const data = await callAPI("notices/notices", "GET", null, true);
        // Transform API data to match frontend format (backend uses PascalCase and enums)
        const transformedNotices = (data || []).map((notice) => ({
          id: notice.id,
          title: notice.title,
          shortDescription: notice.shortDescription,
          details: notice.details,
          priority: getPriorityFromEnum(notice.priority),
          deadline: notice.deadline ? new Date(notice.deadline) : null,
          createdAt: notice.createdAt ? new Date(notice.createdAt) : new Date(),
          updatedAt: notice.updatedAt ? new Date(notice.updatedAt) : null,
          isActive: notice.isActive,
          roleAssignments: (notice.roleAssignments || []).map((ra) => ({
            id: ra.id,
            role: getRoleFromEnum(ra.role),
            personName: ra.personName,
            department: ra.department,
            email: ra.email,
          })),
        }));
        setNotices(transformedNotices);
      } catch (error) {
        console.error("Error fetching notices:", error);
        setNotices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  const handleAddNotice = () => {
    setEditingNotice(null);
    setFormData({
      title: "",
      shortDescription: "",
      details: "",
      priority: "Medium",
      deadline: "",
      roleAssignments: [{ role: "Responsible", personName: "", department: "", email: "" }],
    });
    setEditDialogOpen(true);
  };

  const handleEditNotice = (noticeId) => {
    const notice = notices.find((n) => n.id === noticeId);
    if (notice) {
      setEditingNotice(notice);
      setFormData({
        title: notice.title,
        shortDescription: notice.shortDescription,
        details: notice.details,
        priority: notice.priority,
        deadline: new Date(notice.deadline).toISOString().slice(0, 16), // Format for datetime-local input
        roleAssignments: notice.roleAssignments || [],
      });
      setEditDialogOpen(true);
    }
  };

  const handleSaveNotice = async () => {
    try {
      if (editingNotice) {
        // Update existing notice - send data in format backend expects
        const updateData = {
          title: formData.title,
          shortDescription: formData.shortDescription,
          details: formData.details,
          priority: formData.priority,
          deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
          roleAssignments: formData.roleAssignments
            .filter(
              (assignment) =>
                assignment.personName.trim() !== "" ||
                assignment.department.trim() !== "" ||
                assignment.email.trim() !== ""
            )
            .map((ra) => ({
              role: ra.role,
              personName: ra.personName,
              department: ra.department,
              email: ra.email,
            })),
        };

        const updatedNotice = await callAPI(
          `notices/notices/${editingNotice.id}`,
          "PUT",
          updateData,
          true
        );

        // Transform response back to frontend format
        const transformedNotice = {
          id: updatedNotice.id,
          title: updatedNotice.title,
          shortDescription: updatedNotice.shortDescription,
          details: updatedNotice.details,
          priority: getPriorityFromEnum(updatedNotice.priority),
          deadline: updatedNotice.deadline ? new Date(updatedNotice.deadline) : null,
          createdAt: updatedNotice.createdAt ? new Date(updatedNotice.createdAt) : new Date(),
          updatedAt: updatedNotice.updatedAt ? new Date(updatedNotice.updatedAt) : null,
          isActive: updatedNotice.isActive,
          roleAssignments: (updatedNotice.roleAssignments || []).map((ra) => ({
            id: ra.id,
            role: getRoleFromEnum(ra.role),
            personName: ra.personName,
            department: ra.department,
            email: ra.email,
          })),
        };

        // Update local state
        setNotices((prev) =>
          prev.map((notice) => (notice.id === editingNotice.id ? transformedNotice : notice))
        );
      } else {
        // Add new notice - send data in format backend expects
        const newData = {
          title: formData.title,
          shortDescription: formData.shortDescription,
          details: formData.details,
          priority: formData.priority,
          deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
          roleAssignments: formData.roleAssignments
            .filter(
              (assignment) =>
                assignment.personName.trim() !== "" ||
                assignment.department.trim() !== "" ||
                assignment.email.trim() !== ""
            )
            .map((ra) => ({
              role: ra.role,
              personName: ra.personName,
              department: ra.department,
              email: ra.email,
            })),
        };

        const createdNotice = await callAPI("notices/notices", "POST", newData, true);

        // Transform response back to frontend format
        const transformedNotice = {
          id: createdNotice.id,
          title: createdNotice.title,
          shortDescription: createdNotice.shortDescription,
          details: createdNotice.details,
          priority: getPriorityFromEnum(createdNotice.priority),
          deadline: createdNotice.deadline ? new Date(createdNotice.deadline) : null,
          createdAt: createdNotice.createdAt ? new Date(createdNotice.createdAt) : new Date(),
          updatedAt: createdNotice.updatedAt ? new Date(createdNotice.updatedAt) : null,
          isActive: createdNotice.isActive,
          roleAssignments: (createdNotice.roleAssignments || []).map((ra) => ({
            id: ra.id,
            role: getRoleFromEnum(ra.role),
            personName: ra.personName,
            department: ra.department,
            email: ra.email,
          })),
        };

        // Add to local state
        setNotices((prev) => [...prev, transformedNotice]);
      }
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Error saving notice:", error);
      // You might want to show an error toast here
    }
  };

  const handleDeleteNotice = async (noticeId) => {
    // TODO: Add confirmation dialog
    if (window.confirm("Are you sure you want to delete this notice?")) {
      try {
        await callAPI(`notices/notices/${noticeId}`, "DELETE", null, true);
        setNotices((prev) => prev.filter((notice) => notice.id !== noticeId));
      } catch (error) {
        console.error("Error deleting notice:", error);
        // You might want to show an error toast here
      }
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRoleAssignmentChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      roleAssignments: prev.roleAssignments.map((assignment, i) =>
        i === index ? { ...assignment, [field]: value } : assignment
      ),
    }));
  };

  const addRoleAssignment = () => {
    setFormData((prev) => ({
      ...prev,
      roleAssignments: [
        ...prev.roleAssignments,
        { role: "Responsible", personName: "", department: "", email: "" },
      ],
    }));
  };

  const removeRoleAssignment = (index) => {
    setFormData((prev) => ({
      ...prev,
      roleAssignments: prev.roleAssignments.filter((_, i) => i !== index),
    }));
  };

  const getUrgencyLevel = (deadline) => {
    const now = new Date();
    const daysLeft = Math.floor((new Date(deadline) - now) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) return "overdue";
    if (daysLeft <= 7) return "urgent";
    if (daysLeft <= 30) return "warning";
    return "normal";
  };

  // Filtered and sorted notices using useMemo for performance
  const filteredAndSortedNotices = useMemo(() => {
    let filtered = notices.filter((notice) => {
      // Search filter
      const searchMatch =
        searchTerm === "" ||
        notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notice.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notice.details.toLowerCase().includes(searchTerm.toLowerCase());

      // Priority filter
      const priorityMatch = priorityFilter === "All" || notice.priority === priorityFilter;

      // Urgency filter
      const urgency = getUrgencyLevel(notice.deadline);
      const urgencyMatch = urgencyFilter === "All" || urgency === urgencyFilter;

      return searchMatch && priorityMatch && urgencyMatch;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "title":
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case "priority":
          const priorityOrder = { High: 3, Medium: 2, Low: 1 };
          aValue = priorityOrder[a.priority];
          bValue = priorityOrder[b.priority];
          break;
        case "deadline":
          aValue = new Date(a.deadline);
          bValue = new Date(b.deadline);
          break;
        case "created":
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case "urgency":
          const urgencyOrder = { overdue: 4, urgent: 3, warning: 2, normal: 1 };
          aValue = urgencyOrder[getUrgencyLevel(a.deadline)];
          bValue = urgencyOrder[getUrgencyLevel(b.deadline)];
          break;
        default:
          return 0;
      }

      if (sortOrder === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [notices, searchTerm, priorityFilter, urgencyFilter, sortBy, sortOrder]);

  const clearAllFilters = () => {
    setSearchTerm("");
    setPriorityFilter("All");
    setUrgencyFilter("All");
    setSortBy("deadline");
    setSortOrder("asc");
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: isLight ? "#000" : "#CCC" }}>Loading notices...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search and Filter Controls */}
      <Box sx={{ mb: 2, paddingTop: "20px" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search notices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: isLight ? "#666" : "#aaa" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon sx={{ fontSize: 18, color: isLight ? "#666" : "#aaa" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  color: isLight ? "#000" : "#fff",
                  backgroundColor: isLight ? "#fff" : "#333333",
                  "& fieldset": {
                    borderColor: isLight ? "#ccc" : "#666",
                  },
                  "&:hover fieldset": {
                    borderColor: isLight ? "#999" : "#aaa",
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={3} md={1}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: isLight ? "#666" : "#aaa" }}>Priority</InputLabel>
              <Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                label="Priority"
                sx={{
                  height: "40px",
                  color: isLight ? "#000" : "#fff",
                  backgroundColor: isLight ? "#fff" : "#333333",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isLight ? "#ccc" : "#666",
                  },
                  "& .MuiSvgIcon-root": {
                    color: isLight ? "#666" : "#aaa",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3} md={1}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: isLight ? "#666" : "#aaa" }}>Urgency</InputLabel>
              <Select
                value={urgencyFilter}
                onChange={(e) => setUrgencyFilter(e.target.value)}
                label="Urgency"
                sx={{
                  height: "40px",
                  color: isLight ? "#000" : "#fff",
                  backgroundColor: isLight ? "#fff" : "#333333",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isLight ? "#ccc" : "#666",
                  },
                  "& .MuiSvgIcon-root": {
                    color: isLight ? "#666" : "#aaa",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: isLight ? "#666" : "#aaa" }}>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
                sx={{
                  height: "40px",
                  color: isLight ? "#000" : "#fff",
                  backgroundColor: isLight ? "#fff" : "#333333",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isLight ? "#ccc" : "#666",
                  },
                  "& .MuiSvgIcon-root": {
                    color: isLight ? "#666" : "#aaa",
                  },
                }}
              >
                <MenuItem value="deadline">Deadline</MenuItem>
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="urgency">Urgency</MenuItem>
                <MenuItem value="title">Title</MenuItem>
                <MenuItem value="created">Created Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3} md={0.7}>
            <ToggleButtonGroup
              value={sortOrder}
              exclusive
              onChange={(e, newOrder) => newOrder && setSortOrder(newOrder)}
              sx={{ height: "40px" }}
            >
              <ToggleButton
                value="asc"
                sx={{
                  height: "40px",
                  color: isLight ? "#666" : "#aaa",
                  backgroundColor: isLight ? "#fff" : "#333333",
                  "&.Mui-selected": {
                    backgroundColor: "#FF6C58",
                    color: "white",
                  },
                }}
              >
                ↑
              </ToggleButton>
              <ToggleButton
                value="desc"
                sx={{
                  height: "40px",
                  color: isLight ? "#666" : "#aaa",
                  backgroundColor: isLight ? "#fff" : "#333333",
                  "&.Mui-selected": {
                    backgroundColor: "#FF6C58",
                    color: "white",
                  },
                }}
              >
                ↓
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={3} md={0.8}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearAllFilters}
              startIcon={<ClearIcon />}
              sx={{
                height: "40px",
                color: isLight ? "#666" : "#aaa",
                borderColor: isLight ? "#ccc" : "#666",
                "&:hover": {
                  borderColor: isLight ? "#999" : "#aaa",
                  backgroundColor: isLight ? "#f5f5f5" : "#404040",
                },
              }}
            >
              Clear
            </Button>
          </Grid>
          {checkAccess([...ROLE_CONFIG.admin]) && (
            <Grid item xs={3} md={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNotice}
                sx={{
                  height: "40px",
                  backgroundColor: "#FF6C58",
                  color: "white",
                  fontSize: "0.875rem",
                  "&:hover": {
                    backgroundColor: "#e55a4a",
                  },
                }}
              >
                Add
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          pr: 1,
          pb: 4, // Increased padding bottom for better scroll clearance
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isLight ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: isLight ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.4)",
            },
          },
          // Firefox scrollbar
          scrollbarWidth: "thin",
          scrollbarColor: isLight
            ? "rgba(0,0,0,0.2) transparent"
            : "rgba(255,255,255,0.2) transparent",
          // Add extra margin to ensure last item is fully visible
          "& > *:last-child": {
            marginBottom: "16px",
          },
        }}
      >
        {filteredAndSortedNotices.map((notice) => {
          const urgency = getUrgencyLevel(notice.deadline);

          return (
            <Accordion
              key={notice.id}
              sx={{
                mb: 2,
                backgroundColor: isLight ? "#f9f9f9" : "#414243",
                border: `2px solid ${
                  urgency === "overdue"
                    ? "#FF6C58"
                    : urgency === "urgent"
                      ? "#FFA726"
                      : urgency === "warning"
                        ? "#FFB74D"
                        : isLight
                          ? "#d0d0d0"
                          : "#555555"
                }`,
                "&:before": { display: "none" },
                borderRadius: 1,
                overflow: "hidden",
                color: isLight ? "#000" : "#fff",
                boxShadow:
                  urgency === "overdue"
                    ? "0 0 10px rgba(255, 108, 88, 0.3)"
                    : urgency === "urgent"
                      ? "0 0 8px rgba(255, 167, 38, 0.2)"
                      : urgency === "warning"
                        ? "0 0 6px rgba(255, 183, 77, 0.15)"
                        : "none",
                transition: "box-shadow 0.2s ease-in-out",
                "&.Mui-expanded": {
                  boxShadow:
                    urgency === "overdue"
                      ? "0 0 15px rgba(255, 108, 88, 0.4)"
                      : urgency === "urgent"
                        ? "0 0 12px rgba(255, 167, 38, 0.3)"
                        : urgency === "warning"
                          ? "0 0 10px rgba(255, 183, 77, 0.25)"
                          : `0 0 8px ${isLight ? "rgba(0, 0, 0, 0.15)" : "rgba(255, 255, 255, 0.1)"}`,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: isLight ? "#666" : "#aaa" }} />}
                sx={{
                  backgroundColor: isLight ? "#fafafa" : "#3a3a3a",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        color:
                          notice.priority.toLowerCase() === "high" ||
                          urgency === "overdue" ||
                          urgency === "urgent" ||
                          urgency === "warning"
                            ? urgency === "overdue"
                              ? "#FF6C58"
                              : urgency === "urgent"
                                ? "#FFA726"
                                : urgency === "warning"
                                  ? "#FFB74D"
                                  : "#FF6C58"
                            : "#aaa",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {getPriorityIcon(notice.priority)}
                    </Box>
                    <Chip
                      label={notice.priority}
                      size="small"
                      variant={
                        notice.priority.toLowerCase() === "high" ||
                        urgency === "overdue" ||
                        urgency === "urgent" ||
                        urgency === "warning"
                          ? "filled"
                          : "outlined"
                      }
                      sx={{
                        backgroundColor:
                          notice.priority.toLowerCase() === "high" ||
                          urgency === "overdue" ||
                          urgency === "urgent" ||
                          urgency === "warning"
                            ? urgency === "overdue"
                              ? "#FF6C58"
                              : urgency === "urgent"
                                ? "#FFA726"
                                : urgency === "warning"
                                  ? "#FFB74D"
                                  : "#FF6C58"
                            : "transparent",
                        color:
                          notice.priority.toLowerCase() === "high" ||
                          urgency === "overdue" ||
                          urgency === "urgent" ||
                          urgency === "warning"
                            ? "white"
                            : isLight
                              ? "#666"
                              : "#aaa",
                        borderColor:
                          notice.priority.toLowerCase() === "high" ||
                          urgency === "overdue" ||
                          urgency === "urgent" ||
                          urgency === "warning"
                            ? urgency === "overdue"
                              ? "#FF6C58"
                              : urgency === "urgent"
                                ? "#FFA726"
                                : urgency === "warning"
                                  ? "#FFB74D"
                                  : "#FF6C58"
                            : isLight
                              ? "#ccc"
                              : "#666",
                        fontSize: "0.75rem",
                        fontWeight: "normal",
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: isLight ? "#000" : "#fff",
                        fontSize: "1rem",
                      }}
                    >
                      {notice.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: isLight ? "#666" : "#aaa" }}>
                      {notice.shortDescription}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 280,
                      minWidth: 280,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-end",
                      py: 1,
                    }}
                  >
                    <CountdownTimer deadline={notice.deadline} />
                  </Box>

                  {checkAccess([...ROLE_CONFIG.admin]) && (
                    <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                      <Tooltip title="Edit Notice">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditNotice(notice.id);
                          }}
                          sx={{
                            color: isLight ? "#666" : "#aaa",
                            "&:hover": {
                              backgroundColor: isLight ? "#f0f0f0" : "#555",
                              color: isLight ? "#333" : "#fff",
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Notice">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotice(notice.id);
                          }}
                          sx={{
                            color: isLight ? "#666" : "#aaa",
                            "&:hover": {
                              backgroundColor: "#ffebee",
                              color: "#f44336",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  backgroundColor: isLight ? "#fff" : "#333333",
                  borderTop: `1px solid ${isLight ? "#eee" : "#444"}`,
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: isLight ? "#000" : "#fff", fontSize: "1rem" }}
                    >
                      Details
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ mb: 3, lineHeight: 1.6, color: isLight ? "#333" : "#CCC" }}
                    >
                      {notice.details}
                    </Typography>

                    <Typography variant="caption" sx={{ color: isLight ? "#666" : "#aaa" }}>
                      Created: {new Date(notice.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: isLight ? "#000" : "#fff", fontSize: "1rem" }}
                    >
                      RACI Matrix
                    </Typography>
                    {notice.roleAssignments.map((assignment, index) => {
                      const roleColors = {
                        responsible: { bg: "#1976d2", text: "white", icon: "✓" },
                        accountable: { bg: "#FF6C58", text: "white", icon: "⚡" },
                        consulted: { bg: "#4caf50", text: "white", icon: "💬" },
                        informed: { bg: "#9c27b0", text: "white", icon: "ℹ️" },
                      };
                      const roleStyle =
                        roleColors[assignment.role.toLowerCase()] || roleColors["informed"];

                      return (
                        <Box
                          key={index}
                          sx={{
                            mb: 2,
                            p: 2,
                            backgroundColor: isLight ? "#f8f9fa" : "#2a2a2a",
                            borderRadius: 2,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <Box
                              sx={{
                                backgroundColor: roleStyle.bg,
                                color: roleStyle.text,
                                borderRadius: "50%",
                                width: 24,
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                fontWeight: "bold",
                              }}
                            >
                              {assignment.role.charAt(0)}
                            </Box>
                            <Chip
                              label={assignment.role}
                              size="small"
                              sx={{
                                backgroundColor: roleStyle.bg,
                                color: roleStyle.text,
                                fontSize: "0.7rem",
                                fontWeight: "bold",
                                "& .MuiChip-label": {
                                  px: 1,
                                },
                              }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ color: isLight ? "#000" : "#fff" }}
                            >
                              {assignment.personName}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
                            <PersonIcon sx={{ fontSize: 14, color: isLight ? "#666" : "#aaa" }} />
                            <Typography variant="caption" sx={{ color: isLight ? "#666" : "#aaa" }}>
                              {assignment.department}
                            </Typography>
                          </Box>
                          {assignment.email && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
                              <EmailIcon sx={{ fontSize: 14, color: isLight ? "#666" : "#aaa" }} />
                              <Typography
                                variant="caption"
                                sx={{ color: isLight ? "#666" : "#aaa" }}
                              >
                                {assignment.email}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {filteredAndSortedNotices.length === 0 && !loading && (
          <Card
            sx={{
              backgroundColor: isLight ? "#fff" : "#414243",
              border: `1px solid ${isLight ? "#eee" : "#333333"}`,
            }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <InfoIcon sx={{ fontSize: 48, color: isLight ? "#ccc" : "#666", mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ color: isLight ? "#666" : "#aaa", fontWeight: "normal" }}
              >
                {searchTerm || priorityFilter !== "All" || urgencyFilter !== "All"
                  ? "No notices match your filters"
                  : "No active notices"}
              </Typography>
              <Typography variant="body2" sx={{ color: isLight ? "#999" : "#777" }}>
                {searchTerm || priorityFilter !== "All" || urgencyFilter !== "All"
                  ? "Try adjusting your search terms or filters."
                  : "All systems are up to date with no pending actions."}
              </Typography>
              {(searchTerm || priorityFilter !== "All" || urgencyFilter !== "All") && (
                <Button
                  variant="outlined"
                  onClick={clearAllFilters}
                  sx={{
                    mt: 2,
                    color: isLight ? "#666" : "#aaa",
                    borderColor: isLight ? "#ccc" : "#666",
                  }}
                >
                  Clear All Filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Edit/Add Notice Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isLight ? "#f5f5f5" : "#1f1f1f",
            color: isLight ? "#000" : "#fff",
          },
        }}
      >
        <DialogTitle sx={{ color: isLight ? "#000" : "#fff" }}>
          {editingNotice ? "Edit Notice" : "Add New Notice"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => handleFormChange("title", e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: isLight ? "#000" : "#fff",
                    backgroundColor: isLight ? "#fff" : "#333333",
                    "& fieldset": {
                      borderColor: isLight ? "#ccc" : "#666",
                    },
                    "&:hover fieldset": {
                      borderColor: isLight ? "#999" : "#aaa",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: isLight ? "#666" : "#aaa",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Short Description"
                value={formData.shortDescription}
                onChange={(e) => handleFormChange("shortDescription", e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: isLight ? "#000" : "#fff",
                    backgroundColor: isLight ? "#fff" : "#333333",
                    "& fieldset": {
                      borderColor: isLight ? "#ccc" : "#666",
                    },
                    "&:hover fieldset": {
                      borderColor: isLight ? "#999" : "#aaa",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: isLight ? "#666" : "#aaa",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Details"
                value={formData.details}
                onChange={(e) => handleFormChange("details", e.target.value)}
                variant="outlined"
                multiline
                rows={4}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: isLight ? "#000" : "#fff",
                    backgroundColor: isLight ? "#fff" : "#333333",
                    "& fieldset": {
                      borderColor: isLight ? "#ccc" : "#666",
                    },
                    "&:hover fieldset": {
                      borderColor: isLight ? "#999" : "#aaa",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: isLight ? "#666" : "#aaa",
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: isLight ? "#666" : "#aaa" }}>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => handleFormChange("priority", e.target.value)}
                  label="Priority"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: isLight ? "#fff" : "#2d2d2d",
                        color: isLight ? "#000" : "#fff",
                        "& .MuiMenuItem-root": {
                          color: isLight ? "#000" : "#fff",
                          "&:hover": {
                            backgroundColor: isLight ? "#f5f5f5" : "#404040",
                          },
                          "&.Mui-selected": {
                            backgroundColor: isLight ? "#e3f2fd" : "#333333",
                            "&:hover": {
                              backgroundColor: isLight ? "#bbdefb" : "#404040",
                            },
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    color: isLight ? "#000" : "#fff",
                    backgroundColor: isLight ? "#fff" : "#333333",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isLight ? "#ccc" : "#666",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: isLight ? "#999" : "#aaa",
                    },
                    "& .MuiSvgIcon-root": {
                      color: isLight ? "#666" : "#aaa",
                    },
                  }}
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Deadline"
                type="datetime-local"
                value={formData.deadline}
                onChange={(e) => handleFormChange("deadline", e.target.value)}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  onClick: (e) => {
                    // Focus the input to open the datetime picker
                    e.target.focus();
                    e.target.showPicker && e.target.showPicker();
                  },
                  sx: {
                    cursor: "pointer",
                    "& input": {
                      cursor: "pointer",
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: isLight ? "#000" : "#fff",
                    backgroundColor: isLight ? "#fff" : "#333333",
                    "& fieldset": {
                      borderColor: isLight ? "#ccc" : "#666",
                    },
                    "&:hover fieldset": {
                      borderColor: isLight ? "#999" : "#aaa",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: isLight ? "#666" : "#aaa",
                  },
                  // Encourage browser to use appropriate color scheme for picker
                  '& input[type="datetime-local"]': {
                    colorScheme: isLight ? "light" : "dark",
                  },
                }}
              />
            </Grid>

            {/* RACI Matrix Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: isLight ? "#000" : "#fff", fontSize: "1rem" }}
                >
                  RACI Matrix
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addRoleAssignment}
                  sx={{
                    color: isLight ? "#666" : "#aaa",
                    borderColor: isLight ? "#ccc" : "#666",
                    "&:hover": {
                      borderColor: isLight ? "#999" : "#aaa",
                      backgroundColor: isLight ? "#f5f5f5" : "#404040",
                    },
                  }}
                >
                  Add Role
                </Button>
              </Box>

              {formData.roleAssignments.map((assignment, index) => {
                const roleColors = {
                  responsible: { bg: "#1976d2", text: "white" },
                  accountable: { bg: "#FF6C58", text: "white" },
                  consulted: { bg: "#4caf50", text: "white" },
                  informed: { bg: "#9c27b0", text: "white" },
                };
                const roleStyle =
                  roleColors[assignment.role.toLowerCase()] || roleColors["informed"];

                return (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: isLight ? "#f8f9fa" : "#2a2a2a",
                      borderRadius: 2,
                      border: `2px solid ${roleStyle.bg}`,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: isLight ? "#666" : "#aaa" }}>Role</InputLabel>
                          <Select
                            value={assignment.role}
                            onChange={(e) =>
                              handleRoleAssignmentChange(index, "role", e.target.value)
                            }
                            label="Role"
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  backgroundColor: isLight ? "#fff" : "#2d2d2d",
                                  color: isLight ? "#000" : "#fff",
                                  "& .MuiMenuItem-root": {
                                    color: isLight ? "#000" : "#fff",
                                    "&:hover": {
                                      backgroundColor: isLight ? "#f5f5f5" : "#404040",
                                    },
                                    "&.Mui-selected": {
                                      backgroundColor: isLight ? "#e3f2fd" : "#333333",
                                      "&:hover": {
                                        backgroundColor: isLight ? "#bbdefb" : "#404040",
                                      },
                                    },
                                  },
                                },
                              },
                            }}
                            sx={{
                              color: isLight ? "#000" : "#fff",
                              backgroundColor: isLight ? "#fff" : "#333333",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: isLight ? "#ccc" : "#666",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: isLight ? "#999" : "#aaa",
                              },
                              "& .MuiSvgIcon-root": {
                                color: isLight ? "#666" : "#aaa",
                              },
                            }}
                          >
                            <MenuItem value="Responsible">Responsible</MenuItem>
                            <MenuItem value="Accountable">Accountable</MenuItem>
                            <MenuItem value="Consulted">Consulted</MenuItem>
                            <MenuItem value="Informed">Informed</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Person/Team Name"
                          value={assignment.personName}
                          onChange={(e) =>
                            handleRoleAssignmentChange(index, "personName", e.target.value)
                          }
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              color: isLight ? "#000" : "#fff",
                              backgroundColor: isLight ? "#fff" : "#333333",
                              "& fieldset": {
                                borderColor: isLight ? "#ccc" : "#666",
                              },
                              "&:hover fieldset": {
                                borderColor: isLight ? "#999" : "#aaa",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: isLight ? "#666" : "#aaa",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Department"
                          value={assignment.department}
                          onChange={(e) =>
                            handleRoleAssignmentChange(index, "department", e.target.value)
                          }
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              color: isLight ? "#000" : "#fff",
                              backgroundColor: isLight ? "#fff" : "#333333",
                              "& fieldset": {
                                borderColor: isLight ? "#ccc" : "#666",
                              },
                              "&:hover fieldset": {
                                borderColor: isLight ? "#999" : "#aaa",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: isLight ? "#666" : "#aaa",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Email"
                          value={assignment.email}
                          onChange={(e) =>
                            handleRoleAssignmentChange(index, "email", e.target.value)
                          }
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              color: isLight ? "#000" : "#fff",
                              backgroundColor: isLight ? "#fff" : "#333333",
                              "& fieldset": {
                                borderColor: isLight ? "#ccc" : "#666",
                              },
                              "&:hover fieldset": {
                                borderColor: isLight ? "#999" : "#aaa",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: isLight ? "#666" : "#aaa",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton
                          size="small"
                          onClick={() => removeRoleAssignment(index)}
                          disabled={formData.roleAssignments.length === 1}
                          sx={{
                            color: isLight ? "#666" : "#aaa",
                            "&:hover": {
                              backgroundColor: "#ffebee",
                              color: "#f44336",
                            },
                            "&:disabled": {
                              color: isLight ? "#ccc" : "#555",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: isLight ? "#666" : "#aaa" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveNotice}
            variant="contained"
            disabled={!formData.title || !formData.deadline}
            sx={{
              backgroundColor: "#FF6C58",
              color: "white",
              "&:hover": {
                backgroundColor: "#e55a4a",
              },
              "&:disabled": {
                backgroundColor: isLight ? "#ccc" : "#666",
                color: isLight ? "#999" : "#444",
              },
            }}
          >
            {editingNotice ? "Update Notice" : "Add Notice"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NoticesList;
